import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { CALL_RESULTS, STATUS_ORDER, STATUS } from "@/lib/constants";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { Loader2, PhoneCall, PhoneOff } from "lucide-react";

export function CallLogDialog({ open, onOpenChange, lead, onSaved }) {
  const [reached, setReached] = useState(true);
  const [spoke, setSpoke] = useState(false);
  const [result, setResult] = useState("grundsaetzlich_interessiert");
  const [note, setNote] = useState("");
  const [interested, setInterested] = useState(null);
  const [nextStep, setNextStep] = useState("");
  const [fuDate, setFuDate] = useState("");
  const [fuTime, setFuTime] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [saving, setSaving] = useState(false);
  const qc = useQueryClient();

  const reset = () => {
    setReached(true); setSpoke(false); setResult("grundsaetzlich_interessiert");
    setNote(""); setInterested(null); setNextStep(""); setFuDate(""); setFuTime(""); setNewStatus("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/leads/${lead.id}/call-log`, {
        reached, spoke_to_contact: spoke, result, note,
        interested, next_step: nextStep,
        followup_date: fuDate || null, followup_time: fuTime || null,
        new_status: newStatus || null,
      });
      toast.success("Telefonat dokumentiert");
      reset();
      qc.invalidateQueries();
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  if (!lead) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="call-log-dialog">
        <DialogHeader>
          <DialogTitle>Telefonat dokumentieren</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">{lead.company}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <button type="button" data-testid="call-reached-btn"
              onClick={() => setReached(true)}
              className={cn("flex items-center justify-center gap-2 rounded-lg border py-2.5 text-sm font-medium transition-colors",
                reached ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-300" : "border-border text-muted-foreground")}>
              <PhoneCall className="h-4 w-4" /> Erreicht
            </button>
            <button type="button" data-testid="call-notreached-btn"
              onClick={() => setReached(false)}
              className={cn("flex items-center justify-center gap-2 rounded-lg border py-2.5 text-sm font-medium transition-colors",
                !reached ? "border-rose-500/50 bg-rose-500/15 text-rose-300" : "border-border text-muted-foreground")}>
              <PhoneOff className="h-4 w-4" /> Nicht erreicht
            </button>
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" data-testid="call-spoke-checkbox" checked={spoke}
              onChange={(e) => setSpoke(e.target.checked)} className="h-4 w-4 accent-blue-500" />
            Ansprechpartner gesprochen
          </label>

          <div>
            <Label>Gesprächsergebnis</Label>
            <Select value={result} onValueChange={setResult}>
              <SelectTrigger data-testid="call-result-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CALL_RESULTS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Interesse</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <button type="button" onClick={() => setInterested(true)}
                className={cn("rounded-lg border py-2 text-sm transition-colors",
                  interested === true ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-300" : "border-border text-muted-foreground")}>
                Ja
              </button>
              <button type="button" onClick={() => setInterested(false)}
                className={cn("rounded-lg border py-2 text-sm transition-colors",
                  interested === false ? "border-rose-500/50 bg-rose-500/15 text-rose-300" : "border-border text-muted-foreground")}>
                Nein
              </button>
            </div>
          </div>

          <div>
            <Label>Kurze Notiz</Label>
            <Textarea data-testid="call-note-input" value={note} rows={2}
              onChange={(e) => setNote(e.target.value)} placeholder="Wichtigste Punkte …" />
          </div>

          <div>
            <Label>Nächster Schritt</Label>
            <Input data-testid="call-nextstep-input" value={nextStep}
              onChange={(e) => setNextStep(e.target.value)} placeholder="z.B. Angebot senden" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Follow-up Datum</Label>
              <Input data-testid="call-followup-date" type="date" value={fuDate}
                onChange={(e) => setFuDate(e.target.value)} />
            </div>
            <div>
              <Label>Uhrzeit (optional)</Label>
              <Input data-testid="call-followup-time" type="time" value={fuTime}
                onChange={(e) => setFuTime(e.target.value)} />
            </div>
          </div>

          <div>
            <Label>Status ändern (optional)</Label>
            <Select value={newStatus} onValueChange={setNewStatus}>
              <SelectTrigger data-testid="call-status-select"><SelectValue placeholder="Status beibehalten" /></SelectTrigger>
              <SelectContent>
                {STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{STATUS[s].label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Abbrechen</Button>
            <Button type="submit" data-testid="call-submit-btn" disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Speichern
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
